import {ToISOTimeOptions} from "luxon";
import toDateTime from "./toDateTime";

/**
 * The documentation for the `precision` option in Luxon's `toISO` method is exists but the type definitions
 * do not include it. This interface extends the existing `ToISOTimeOptions` to include
 * the `precision` option, which allows specifying the level of detail in the ISO string.
 *
 * @see https://moment.github.io/luxon/api-docs/index.html#datetimetoiso
 */
interface ExtendedToISOTimeOptions extends ToISOTimeOptions {
    precision?:
        | "years"
        | "months"
        | "days"
        | "hours"
        | "minutes"
        | "seconds"
        | "milliseconds";
}

/**
 * Parses a date string into a string in ISO format. As Javascript's Date object does not handle time zones well,
 * this function uses Luxon to ensure the date is formatted correctly. Javascript also use colon in the time zone offset.
 * So JS uses RFC3339, and PHP uses ISO8601, which is the same except for the colon in the time zone offset.
 *
 * @param {any} date - The date to parse.
 * @param {string} timezone - The timezone
 * @returns {string} - The ISO formatted date string or an empty string if invalid.
 */
const formatISO = (date: any, timezone: string = "Europe/Berlin"): string => {
    let inputDate = toDateTime(date);
    if (!inputDate.isValid) {
        return "";
    }

    let isoFormat = inputDate
        .setZone(timezone)
        .toISO({precision: "seconds"} as ExtendedToISOTimeOptions);
    return isoFormat ? isoFormat.replace(/([+-]\d{2}):?(\d{2})$/, "$1$2") : "";
};

export default formatISO;
